import { useState } from "react";
import {
  createUserWithEmailAndPassword, signInWithEmailAndPassword,
  signInWithPopup, updateProfile,
} from "firebase/auth";
import { auth, googleProvider } from "../firebase";
import { D } from "../constants";

function friendlyError(code) {
  const m = {
    "auth/email-already-in-use": "Ese correo ya tiene una cuenta. Inicia sesión.",
    "auth/invalid-email":        "El correo no tiene un formato válido.",
    "auth/weak-password":        "La contraseña debe tener al menos 6 caracteres.",
    "auth/user-not-found":       "No existe una cuenta con ese correo.",
    "auth/wrong-password":       "Contraseña incorrecta. Intenta de nuevo.",
    "auth/invalid-credential":   "Correo o contraseña incorrectos.",
    "auth/too-many-requests":    "Demasiados intentos. Espera unos minutos.",
    "auth/popup-closed-by-user": "Cerraste la ventana de Google. Intenta de nuevo.",
    "auth/network-request-failed": "Error de red. Revisa tu conexión.",
  };
  return m[code] || "Ocurrió un error inesperado. Intenta de nuevo.";
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" style={{ flexShrink: 0 }}>
      <path fill="#EA4335" d="M24 9.5c3.14 0 5.95 1.08 8.17 2.86l6.08-6.08C34.36 2.99 29.45 1 24 1 14.82 1 7.08 6.48 3.55 14.24l7.1 5.52C12.36 13.48 17.68 9.5 24 9.5z"/>
      <path fill="#4285F4" d="M46.1 24.55c0-1.64-.15-3.22-.42-4.75H24v9h12.42c-.54 2.87-2.17 5.3-4.62 6.94l7.1 5.52C43.26 37.13 46.1 31.3 46.1 24.55z"/>
      <path fill="#FBBC05" d="M10.65 28.24A14.6 14.6 0 019.5 24c0-1.48.25-2.91.65-4.24l-7.1-5.52A23.84 23.84 0 001 24c0 3.87.92 7.53 2.55 10.76l7.1-5.52z"/>
      <path fill="#34A853" d="M24 47c5.45 0 10.02-1.8 13.36-4.89l-7.1-5.52C28.55 38.4 26.4 39.5 24 39.5c-6.32 0-11.64-3.98-13.35-9.54l-7.1 5.52C7.08 43.52 14.82 47 24 47z"/>
    </svg>
  );
}

function DarkField({ label, type = "text", value, onChange, placeholder, onEnter }) {
  const [focus, setFocus] = useState(false);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {label && (
        <label style={{ fontSize: 11, fontFamily: "'DM Mono',monospace", color: D.textDim, fontWeight: 700, letterSpacing: 0.5 }}>
          {label}
        </label>
      )}
      <input
        type={type} value={value} onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        onKeyDown={e => e.key === "Enter" && onEnter && onEnter()}
        onFocus={() => setFocus(true)} onBlur={() => setFocus(false)}
        style={{
          width: "100%", padding: "11px 14px", borderRadius: 6,
          background: "#0F0D0A", border: `1px solid ${focus ? D.gold : D.borderMid}`,
          color: D.text, fontSize: 15, outline: "none", fontWeight: 700,
          fontFamily: "'Source Serif 4',serif",
        }}
      />
    </div>
  );
}

export default function AuthModal({ initialTab, onAuth, onClose }) {
  const [tab,     setTab]     = useState(initialTab);
  const [name,    setName]    = useState("");
  const [email,   setEmail]   = useState("");
  const [pass,    setPass]    = useState("");
  const [loading, setLoading] = useState(false);
  const [gLoad,   setGLoad]   = useState(false);
  const [error,   setError]   = useState(null);
  const clear = () => setError(null);

  const handleEmail = async () => {
    clear();
    if (!email.trim() || !pass.trim()) { setError("Completa todos los campos."); return; }
    if (tab === "register" && !name.trim()) { setError("Escribe tu nombre completo."); return; }
    setLoading(true);
    try {
      let cred;
      if (tab === "register") {
        cred = await createUserWithEmailAndPassword(auth, email.trim(), pass);
        await updateProfile(cred.user, { displayName: name.trim() });
      } else {
        cred = await signInWithEmailAndPassword(auth, email.trim(), pass);
      }
      onAuth(cred.user);
    } catch (e) { setError(friendlyError(e.code)); }
    setLoading(false);
  };

  const handleGoogle = async () => {
    clear(); setGLoad(true);
    try { const r = await signInWithPopup(auth, googleProvider); onAuth(r.user); }
    catch (e) { setError(friendlyError(e.code)); }
    setGLoad(false);
  };

  return (
    <div
      onClick={e => e.target === e.currentTarget && onClose()}
      style={{
        position: "fixed", inset: 0, zIndex: 200, background: "rgba(0,0,0,0.75)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "20px 16px", backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)",
      }}
    >
      <div className="fade-up" style={{
        width: "100%", maxWidth: 420, background: D.card,
        border: `1px solid ${D.borderMid}`, borderRadius: 10,
        padding: "clamp(28px,6vw,44px) clamp(24px,6vw,40px)", position: "relative",
      }}>
        <button onClick={onClose} style={{ position: "absolute", top: 14, right: 16, fontSize: 18, color: D.textDim, lineHeight: 1, padding: "2px 6px" }}>✕</button>
        <h2 style={{ fontFamily: "'Playfair Display',serif", fontWeight: 900, fontSize: 24, color: D.text, marginBottom: 6 }}>
          {tab === "login" ? "Bienvenido de vuelta" : "Crear cuenta"}
        </h2>
        <p style={{ fontSize: 13, color: D.textDim, fontWeight: 700, marginBottom: 26 }}>
          {tab === "login" ? "Ingresa a tu sesión de LitisLab." : "Es gratis durante la beta."}
        </p>

        <div style={{ display: "flex", borderBottom: `1px solid ${D.borderMid}`, marginBottom: 24 }}>
          {[{ k: "login", label: "Iniciar sesión" }, { k: "register", label: "Crear cuenta" }].map(({ k, label }) => (
            <button key={k} onClick={() => { setTab(k); clear(); }} style={{
              flex: 1, padding: "10px 0", fontSize: 14, fontFamily: "'Source Serif 4',serif",
              fontWeight: 900, color: tab === k ? D.text : D.textDim,
              borderBottom: `2px solid ${tab === k ? D.gold : "transparent"}`, marginBottom: -1,
            }}>
              {label}
            </button>
          ))}
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <button
            onClick={handleGoogle} disabled={gLoad}
            style={{
              width: "100%", padding: "12px 16px", borderRadius: 6, background: "#1A1810",
              border: `1px solid ${D.borderMid}`, display: "flex", alignItems: "center",
              justifyContent: "center", gap: 10, fontSize: 15, fontWeight: 700,
              color: D.text, fontFamily: "'Source Serif 4',serif",
            }}
            onMouseEnter={e => e.currentTarget.style.borderColor = D.textDim}
            onMouseLeave={e => e.currentTarget.style.borderColor = D.borderMid}
          >
            {gLoad
              ? <div style={{ width: 18, height: 18, border: `2px solid ${D.borderMid}`, borderTopColor: D.gold, borderRadius: "50%", animation: "spin .8s linear infinite" }}/>
              : <GoogleIcon/>
            }
            {gLoad ? "Conectando…" : "Continuar con Google"}
          </button>

          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ flex: 1, height: 1, background: D.border }}/>
            <span style={{ fontSize: 10, color: D.textFaint, fontFamily: "'DM Mono',monospace", fontWeight: 700 }}>O CON CORREO</span>
            <div style={{ flex: 1, height: 1, background: D.border }}/>
          </div>

          {tab === "register" && (
            <DarkField label="Nombre completo" value={name} onChange={setName} placeholder="Dra. Ana Suárez Pinto"/>
          )}
          <DarkField label="Correo electrónico" type="email" value={email} onChange={v => { setEmail(v); clear(); }} placeholder="tucorreo@ejemplo.com"/>
          <DarkField label="Contraseña" type="password" value={pass} onChange={v => { setPass(v); clear(); }}
            placeholder={tab === "register" ? "Mínimo 6 caracteres" : "Tu contraseña"} onEnter={handleEmail}/>

          {error && (
            <div style={{ padding: "10px 14px", background: "#280A0A", border: "1px solid #5A1A1A", borderRadius: 6 }}>
              <span style={{ fontSize: 13, color: "#E07070", fontWeight: 700 }}>{error}</span>
            </div>
          )}

          <button onClick={handleEmail} disabled={loading} style={{
            padding: "13px", borderRadius: 6, background: loading ? D.borderMid : D.gold,
            color: loading ? D.textDim : "#080705", fontSize: 16, fontWeight: 900,
            fontFamily: "'Playfair Display',serif", marginTop: 4,
          }}>
            {loading
              ? (tab === "login" ? "Ingresando…" : "Creando cuenta…")
              : (tab === "login" ? "Ingresar a LitisLab →" : "Crear mi cuenta →")
            }
          </button>
        </div>

        <p style={{ marginTop: 20, fontSize: 12, color: D.textDim, textAlign: "center", fontFamily: "'DM Mono',monospace", fontWeight: 700 }}>
          {tab === "login"
            ? <span>¿No tienes cuenta? <button onClick={() => { setTab("register"); clear(); }} style={{ color: D.gold, fontWeight: 700, fontSize: 12, fontFamily: "'DM Mono',monospace", textDecoration: "underline" }}>Regístrate gratis</button></span>
            : <span>¿Ya tienes cuenta? <button onClick={() => { setTab("login"); clear(); }} style={{ color: D.gold, fontWeight: 700, fontSize: 12, fontFamily: "'DM Mono',monospace", textDecoration: "underline" }}>Inicia sesión</button></span>
          }
        </p>
      </div>
    </div>
  );
}
